// Abort errors reach us wrapped (`GatewayResponseError` → `ResponseAborted`
// → DOMException `AbortError` / `TimeoutError`) depending on the provider, so
// we unwrap `.cause` a bounded number of times rather than trust one layer's
// `.name`.
//
// Intentionally avoid `instanceof Error`: jsdom's `DOMException` is not an
// `Error` subclass, while Node's is. Production `AbortSignal.timeout()` rejects
// with a `TimeoutError` DOMException.
export function isAbortError(error: unknown): boolean {
  for (let cursor: unknown = error, depth = 0; depth < 5; depth++) {
    if (cursor == null || typeof cursor !== 'object') return false;

    const name = 'name' in cursor ? String(cursor.name) : '';
    const message = 'message' in cursor ? String(cursor.message) : '';

    if (
      name === 'AbortError' ||
      name === 'ResponseAborted' ||
      // `AbortSignal.timeout()` rejects with DOMException name `TimeoutError`
      // and message "The operation was aborted due to timeout".
      name === 'TimeoutError'
    ) {
      return true;
    }
    if (/abort/i.test(message)) return true;

    cursor = 'cause' in cursor ? cursor.cause : undefined;
  }
  return false;
}

/**
 * True when an error matches undici's consumer-gone / socket-closed
 * signatures (`TypeError: terminated`, `SocketError: other side closed`)
 * or a wrapped abort.
 *
 * This is a signature classifier, not a verdict that the failure is
 * benign. Next.js pipe failures after navigation use these signatures
 * (PORTAL-1F2); an upstream model reader can throw the same errors when
 * the provider drops mid-stream. Callers must distinguish those cases
 * (e.g. `abortSignal.aborted` / stream `cancel()` vs. a live consumer).
 */
export function isStreamDisconnectError(error: unknown): boolean {
  if (isAbortError(error)) return true;

  for (let cursor: unknown = error, depth = 0; depth < 5; depth++) {
    if (cursor == null || typeof cursor !== 'object') return false;

    const name = 'name' in cursor ? String(cursor.name) : '';
    const message = 'message' in cursor ? String(cursor.message) : '';

    if (message === 'terminated') return true;
    if (name === 'SocketError' && message.includes('other side closed')) {
      return true;
    }

    cursor = 'cause' in cursor ? cursor.cause : undefined;
  }
  return false;
}
